import { useCallback, useEffect, useRef, useState } from "react";
import { AdminHeader } from "@trillion/ui/components/admin/admin-header";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { Button } from "@trillion/ui/components/ui/button";
import { Skeleton } from "@trillion/ui/components/ui/skeleton";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { JobDefinitionCard } from "@/components/jobs/job-definition-card";
import { JobSettingsCard } from "@/components/jobs/job-settings-card";
import { jobLabel } from "@/components/runs/run-columns";
import { RunDaysSelector } from "@/components/runs/run-days-selector";
import { RunLogView } from "@/components/runs/run-log-panel";
import { usePollWhile } from "@/hooks/use-poll-while";
import { POLL_INTERVAL_MS, useRecurringJobs } from "@/hooks/use-recurring-jobs";
import { shouldPoll } from "@/lib/job-states";
import { jobsApi } from "@/lib/admin-api";
import { LIVE_JOB_STATE, nextLiveRunId } from "@/lib/job-details";
import type { RecurringJob } from "@/lib/jobs-types";
import { readRunDays, withRunDays, type RunDays } from "@/lib/run-days";
import { FailedRunsTab, SucceededRunsTab } from "@/pages/runs";

/**
 * The routing boundary for `/jobs/:recurringJobId?days=N` (sc-114201): the route param names the job and `days`
 * the history window, which an unsupported value drops in one `replace`. The view below is keyed by the id, so a
 * different job is always a fresh view — its own live panel, pending overlay and history.
 */
export function JobDetailsPage() {
  const { recurringJobId = "" } = useParams<{ recurringJobId: string }>();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const days = readRunDays(search);

  const selectDays = useCallback(
    (next: RunDays) => {
      void navigate({ pathname, search: withRunDays(search, next) }, { replace: true });
    },
    [navigate, pathname, search]
  );

  if (days === null) {
    return <Navigate to={{ pathname, search: withRunDays(search, null) }} replace />;
  }

  return (
    <JobDetails key={recurringJobId} recurringJobId={recurringJobId} days={days} onDaysChange={selectDays} />
  );
}

/**
 * The run the inline live log shows, and which attempt of it. A requeue reuses its job id (epic 🧭 2026-09-19:
 * logs append across attempts), so the view is keyed by `id:attempt` — a requeue of the run already shown
 * bumps `attempt` and restarts its tail from cursor 0, while a poll never remounts the same id.
 */
interface LiveRun {
  id: string | null;
  attempt: number;
}

/** True when this job's own run is Processing — that run keeps the Live log (the requeue precedence rule). */
function isRunningOwnRun(job: RecurringJob | undefined): boolean {
  return job?.last_job_state === LIVE_JOB_STATE && Boolean(job.last_job_id);
}

/**
 * One recurring job, composed client-side (epic 🧭 2026-09-19): the definition from `recurring`, the history
 * from the job-filtered run lists, the `job:<id>` settings, and the live tail while it runs. Budget: ≤ 5 `admin`
 * requests on load (recurring; then succeeded, failed, settings, and the active-retry lookup unless the job's own
 * run is Processing), plus the tail of whichever run is live on its own `admin-tail` bucket; while running, only
 * the 3 s recurring poll and that tail. The history and settings never poll.
 */
function JobDetails({
  recurringJobId,
  days,
  onDaysChange,
}: {
  recurringJobId: string;
  days: RunDays;
  onDaysChange: (days: RunDays) => void;
}) {
  const { data, isLoading, loaded, triggering, pending, refresh, triggerJob } = useRecurringJobs();
  const job = data.find((row) => row.id === recurringJobId);
  const isPending = pending.has(recurringJobId);

  // The definition as last loaded, read by the requeue handler without re-creating it on every poll.
  const jobRef = useRef(job);
  useEffect(() => {
    jobRef.current = job;
  }, [job]);

  // Only this job's state and pending entry keep the recurring poll alive — never another job's.
  usePollWhile({
    enabled: shouldPoll(job ? [job] : [], isPending ? 1 : 0),
    intervalMs: POLL_INTERVAL_MS,
    onTick: refresh,
  });

  // Sticky once seen, so the panel outlives the definition leaving Processing and stops on its own tail's
  // `finished` flag. A newer Processing run replaces the shown one without an attempt bump.
  const [live, setLive] = useState<LiveRun>({ id: null, attempt: 0 });
  const lastJobId = job?.last_job_id ?? null;
  const lastJobState = job?.last_job_state ?? null;
  useEffect(() => {
    setLive((current) => {
      const id = nextLiveRunId(current.id, { last_job_id: lastJobId, last_job_state: lastJobState });
      return id === current.id ? current : { id, attempt: current.attempt };
    });
  }, [lastJobId, lastJobState]);

  // An older run requeued from Run History or another client leaves the definition's last_job_id on the newer
  // run, so the pair above never names it. One job-filtered Processing read adopts such a retry, made only when
  // the job's own run is not Processing (that run keeps the Live log) and only once per view. Best-effort: a
  // failed lookup leaves the page as it would be without it.
  const retryLookupDone = useRef(false);
  const jobFound = job !== undefined;
  const ownRunRunning = isRunningOwnRun(job);
  useEffect(() => {
    if (!jobFound || ownRunRunning || retryLookupDone.current) return;
    retryLookupDone.current = true;
    jobsApi
      .processingFor(recurringJobId)
      .then((rows) => {
        const retryId = rows[0]?.job_id;
        if (retryId) setLive((current) => (current.id === null ? { id: retryId, attempt: current.attempt } : current));
      })
      .catch(() => {});
  }, [jobFound, ownRunRunning, recurringJobId]);

  // A Requeue in the embedded Failed grid. This job's own Processing run keeps the Live log (a requeue leaves
  // the recurring hash untouched, so `nextLiveRunId` would keep adopting that run); otherwise the Live log
  // follows the requeued attempt, remounting it even when it is the run already shown. Either way one refresh
  // lets the card's Last state follow the retry.
  const followRequeue = useCallback(
    (jobId: string) => {
      if (!isRunningOwnRun(jobRef.current)) {
        setLive((current) => ({ id: jobId, attempt: current.attempt + 1 }));
      }
      void refresh();
    },
    [refresh]
  );

  const runNow = useCallback(() => {
    void triggerJob(recurringJobId);
  }, [triggerJob, recurringJobId]);

  const liveJobId = live.id;
  const liveKey = `${live.id}:${live.attempt}`;

  let body;
  if (!loaded) {
    body = isLoading ? (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    ) : (
      <p className="py-12 text-center text-sm text-destructive">Could not load recurring jobs.</p>
    );
  } else if (!job) {
    body = (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <p className="text-sm text-muted-foreground">Recurring job not found</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/jobs">Back to Recurring Jobs</Link>
        </Button>
      </div>
    );
  } else {
    body = (
      <>
        <PageHeader title={job.id} description={jobLabel(job.job)} />
        <JobDefinitionCard
          job={job}
          isTriggering={triggering.has(job.id)}
          isPending={isPending}
          onRunNow={runNow}
        />

        {liveJobId !== null && (
          <section aria-label="Live log" className="flex h-96 flex-col gap-2 rounded-lg border bg-card pt-4">
            <div className="flex items-baseline gap-2 px-4">
              <h2 className="text-lg font-semibold leading-none">Live log</h2>
              <span className="font-mono text-xs text-muted-foreground">{liveJobId}</span>
            </div>
            <RunLogView key={liveKey} jobId={liveJobId} />
          </section>
        )}

        <section aria-label="Run history" className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Run history</h2>
            <RunDaysSelector value={days} onChange={onDaysChange} />
          </div>
          <SucceededRunsTab key={`succeeded-${days}`} days={days} recurringJobId={job.id} />
          <FailedRunsTab
            key={`failed-${days}`}
            days={days}
            recurringJobId={job.id}
            onRequeued={followRequeue}
          />
        </section>

        <JobSettingsCard recurringJobId={job.id} />
      </>
    );
  }

  return (
    <div className="flex flex-col">
      <AdminHeader
        title={recurringJobId}
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Recurring Jobs", href: "/jobs" },
          { label: recurringJobId },
        ]}
      />
      <div className="flex flex-col gap-6 p-6">{body}</div>
    </div>
  );
}
